// The 2D platformer runtime scene: high-definition procedural tilemaps with
// rich multi-pass material shaders, state-driven multi-clip character animations,
// ghost after-image trails, kinematic squash-and-stretch, chapter-specific 5-layer
// parallax atmospheric skyboxes, and an orthographic camera with lookahead.
import { OrthographicCamera } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';


import type { Sprite2D, Tilemap2D } from '../../content/schema/map';
import type { EnemyRuntime, Projectile, WeaponSpec } from './combat2d';
import { ENEMY_DEATH_HOLD_SECONDS } from './enemies2d';
import { appendParticles, particlesForEvent, stepParticles, type Particle2D } from './particles2d';
import { DEFAULT_PLATFORMER_BINDINGS, mergePlatformerBindings, type PlatformerBindings, type PlatformerBindingsOverrides } from './platformerBindings';
import {
  acquireKeyboardInput,
  clearJustPressedKeys,
  getConnectedGamepads,
  getPrimaryGamepad,
  isKeyDown,
  isKeyJustPressed,
  isTypingIntoFormField,
  releaseKeyboardInput,
} from '../../input/inputState';
import {
  buildPlatformerGrid,
  createInitialPlatformerState,
  DEFAULT_PLATFORMER_CONFIG,
  dynamicEntityPosition,
  PLAYER_HALF_HEIGHT,
  stepPlatformer,
  TILE_SIZE,
  type DynamicEntity,
  type PlatformerAbilities,
  type PlatformerConfig,
  type PlatformerEvent,
  type PlatformerInput,
  type PlatformerState,
} from './platformer2d';
import { canvasTexture, drawSprite, drawSpriteCells, paintSpriteCellsAt } from './spriteCanvas';
import { spriteAnimationCells } from './sprite2d';
import { PatrollingEntities, WorldEntities } from './Tilemap2DScene';

const PIXELS_PER_TILE = 48;

/** Tiles per chunk edge. A chunk is one 512 px canvas at 32 px per tile. */
const CHUNK_TILES = 16;
/** Hard cap on map area; past this the per-chunk canvases alone would cost
 * hundreds of megabytes. Nine Lives' longest planned level is 320 by 60. */
const MAX_MAP_CELLS = 100000;
/** Physics runs at a fixed 60 Hz regardless of display refresh. */
const FIXED_DT = 1 / 60;
const MAX_STEPS_PER_FRAME = 5;

interface ChunkRegion {
  x0: number;
  y0: number;
  w: number;
  h: number;
}

interface TileChunk {
  region: ChunkRegion;
  texture: THREE.CanvasTexture | null;
}

const chunkCache = new WeakMap<Tilemap2D, { main: TileChunk[]; fg: TileChunk[] }>();
const parallaxFarCache = new Map<string, THREE.CanvasTexture>();
const parallaxMidCache = new Map<string, THREE.CanvasTexture>();
const parallaxWeatherCache = new Map<string, THREE.CanvasTexture>();
const bakedBackdropCache = new Map<string, THREE.Texture>();
const backdropTextureLoader = new THREE.TextureLoader();

/** Loads (and caches by URL) a baked backdrop image as a crisp, non-mipmapped
 * pixel-art texture - the same filtering/colour-space `canvasTexture` applies
 * to every procedurally-drawn texture in this file, kept consistent here so a
 * baked backdrop doesn't look softer than its procedural neighbours. Loading
 * is async; `THREE.Texture` updates in place once the image arrives and this
 * scene's own `useFrame` loop (already running continuously for camera
 * follow/parallax) picks up the change on its next frame with no extra
 * plumbing. */
function loadBakedTexture(url: string): THREE.Texture {
  const cached = bakedBackdropCache.get(url);
  if (cached) return cached;
  const texture = backdropTextureLoader.load(url);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.colorSpace = THREE.SRGBColorSpace;
  bakedBackdropCache.set(url, texture);
  return texture;
}

/** Render high-definition procedural textures for tilemap layers with 32px texels. */
function drawTilemap(map: Tilemap2D, region: ChunkRegion = { x0: 0, y0: 0, w: map.width, h: map.height }): HTMLCanvasElement {
  const px = 32; // Optimized crisp texels per tile
  const canvas = document.createElement('canvas');
  canvas.width = region.w * px;
  canvas.height = region.h * px;
  const ctx = canvas.getContext('2d')!;
  const byId = new Map(map.tiles.map((t) => [t.id, t]));


  // Helper for drawing a single tile layer
  const renderLayer = (cells: (string | null)[], isBackground = false) => {
    for (let y = region.y0; y < region.y0 + region.h; y++) {
      for (let x = region.x0; x < region.x0 + region.w; x++) {
        const tileId = cells[y * map.width + x];
        if (!tileId) continue;
        const tile = byId.get(tileId);
        if (!tile) continue;

        const tx = (x - region.x0) * px;
        const ty = (y - region.y0) * px;

        if (isBackground) {
          if (tile.art) {
            // Baked art still gets the dimmed-depth treatment on top, so a
            // background silhouette reads as further away than the main
            // layer even once it is real pixel art rather than a flat
            // colour block.
            paintSpriteCellsAt(ctx, tile.art.size, tile.art.cells, tx, ty, px / tile.art.size);
            ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
            ctx.fillRect(tx, ty, px, px);
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.04)';
            ctx.lineWidth = 1;
            ctx.strokeRect(tx + 2, ty + 2, px - 4, px - 4);
            continue;
          }
          // Dimmed backdrop styling for interior depth
          ctx.fillStyle = tile.b || '#0b0f19';
          ctx.fillRect(tx, ty, px, px);
          ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
          ctx.fillRect(tx, ty, px, px);
          // Subtle structural lines
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.04)';
          ctx.lineWidth = 1;
          ctx.strokeRect(tx + 2, ty + 2, px - 4, px - 4);
          continue;
        }

        if (tile.art) {
          // Baked pixel art is the single source of truth for this tile's
          // appearance when present - paint it and skip both the
          // procedural `pattern` texturing and the `kind`-based gameplay
          // decoration below entirely, the same "instead of the flat a/b
          // checker" contract `Tilemap2DScene.tsx`'s `paintLayer` already
          // established for this field. Ambient glow (`PointLightRadianceMesh`)
          // is a separate pass and still applies on top regardless.
          paintSpriteCellsAt(ctx, tile.art.size, tile.art.cells, tx, ty, px / tile.art.size);
          continue;
        }

        // Base tile fill with soft gradient
        const baseGrad = ctx.createLinearGradient(tx, ty, tx, ty + px);
        baseGrad.addColorStop(0, tile.a);
        baseGrad.addColorStop(1, tile.b || tile.a);
        ctx.fillStyle = baseGrad;
        ctx.fillRect(tx, ty, px, px);

        const pattern = tile.pattern || tile.id;

        // Rich procedural texturing based on theme / material
        if (pattern.includes('slate')) {
          // Victorian slate shingles with specular top bevels and wet sheen
          const shingleH = px / 2;
          for (let row = 0; row < 2; row++) {
            const sy = ty + row * shingleH;
            const xShift = (row + (y % 2)) % 2 === 0 ? 0 : px / 2;

            // Shingle plate fill
            ctx.fillStyle = row === 0 ? tile.a : tile.b;
            ctx.fillRect(tx, sy, px, shingleH);

            // Shingle top highlight bevel
            ctx.fillStyle = tile.accent || 'rgba(148, 163, 184, 0.6)';
            ctx.fillRect(tx, sy, px, 3);

            // Shingle bottom shadow edge
            ctx.fillStyle = tile.border || '#090d16';
            ctx.fillRect(tx, sy + shingleH - 2, px, 2);

            // Shingle vertical slit division
            ctx.fillRect(tx + xShift, sy, 2, shingleH);

            // Wet specular raindrop sheen
            ctx.fillStyle = 'rgba(255, 255, 255, 0.18)';
            ctx.fillRect(tx + xShift + 6, sy + 4, 10, 2);
          }
        } else if (pattern.includes('brick')) {
          // Terracotta brickwork with mortar depth
          const bH = px / 4;
          const bW = px / 2;
          for (let row = 0; row < 4; row++) {
            const by = ty + row * bH;
            const shift = (row % 2 === 0 ? 0 : bW / 2);

            // Mortar line
            ctx.fillStyle = tile.border || '#271206';
            ctx.fillRect(tx, by, px, 2);

            // Individual bricks
            for (let col = -1; col < 3; col++) {
              const bx = tx + col * bW + shift;
              ctx.fillStyle = tile.a;
              ctx.fillRect(bx + 1, by + 2, bW - 2, bH - 3);

              // Brick top highlight
              ctx.fillStyle = tile.accent || '#b45309';
              ctx.fillRect(bx + 1, by + 2, bW - 2, 2);

              // Brick vertical mortar
              ctx.fillStyle = tile.border || '#271206';
              ctx.fillRect(bx, by, 2, bH);
            }
          }
        } else if (pattern.includes('girder') || pattern.includes('iron')) {
          // Industrial riveted iron steel plates with cross struts
          ctx.fillStyle = tile.b;
          ctx.fillRect(tx + 6, ty + 6, px - 12, px - 12);

          // Steel cross-brace
          ctx.strokeStyle = tile.accent || '#64748b';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(tx + 8, ty + 8);
          ctx.lineTo(tx + px - 8, ty + px - 8);
          ctx.moveTo(tx + px - 8, ty + 8);
          ctx.lineTo(tx + 8, ty + px - 8);
          ctx.stroke();

          // Outer steel border & inner bevel
          ctx.fillStyle = tile.border || '#1e293b';
          ctx.strokeRect(tx + 2, ty + 2, px - 4, px - 4);
          ctx.strokeRect(tx + 6, ty + 6, px - 12, px - 12);

          // Metallic rivets with specular glints
          const rivetColor = tile.accent || '#94a3b8';
          const rivetShine = '#ffffff';
          const rivets: [number, number][] = [
            [tx + 4, ty + 4],
            [tx + px - 7, ty + 4],
            [tx + 4, ty + px - 7],
            [tx + px - 7, ty + px - 7],
            [tx + px / 2 - 2, ty + 4],
            [tx + px / 2 - 2, ty + px - 7],
          ];
          for (const [rx, ry] of rivets) {
            ctx.fillStyle = rivetColor;
            ctx.fillRect(rx, ry, 4, 4);
            ctx.fillStyle = rivetShine;
            ctx.fillRect(rx + 1, ry + 1, 1, 1);
          }
        } else if (pattern.includes('gothic') || pattern.includes('stone')) {
          // Aged gothic masonry with cracked stone and moss
          ctx.fillStyle = tile.b;
          ctx.fillRect(tx + 4, ty + 4, px - 8, px - 8);

          // Carved stone cracks
          ctx.strokeStyle = '#05030a';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(tx + 12, ty + 6);
          ctx.lineTo(tx + 28, ty + 26);
          ctx.lineTo(tx + 48, ty + 38);
          ctx.moveTo(tx + 28, ty + 26);
          ctx.lineTo(tx + 18, ty + 50);
          ctx.stroke();

          // Top moss trim
          ctx.fillStyle = '#065f46';
          ctx.fillRect(tx + 2, ty + 2, px - 4, 3);
          ctx.fillStyle = '#10b981';
          ctx.fillRect(tx + 6, ty + 3, 8, 2);
          ctx.fillRect(tx + 32, ty + 3, 12, 2);

          // Masonry border
          ctx.strokeStyle = tile.accent || '#334155';
          ctx.strokeRect(tx + 2, ty + 2, px - 4, px - 4);
        } else if (pattern.includes('gear') || pattern.includes('brass')) {
          // Clockwork brass horology plates with etched gear rings
          const cx = tx + px / 2;
          const cy = ty + px / 2;

          // Outer gear circle
          ctx.fillStyle = tile.b;
          ctx.beginPath();
          ctx.arc(cx, cy, px / 2 - 6, 0, Math.PI * 2);
          ctx.fill();

          // Concentric gold horology ring
          ctx.strokeStyle = tile.accent || '#f59e0b';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.arc(cx, cy, px / 2 - 14, 0, Math.PI * 2);
          ctx.stroke();

          // Central brass pivot rivet
          ctx.fillStyle = '#fbbf24';
          ctx.beginPath();
          ctx.arc(cx, cy, 6, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(cx - 2, cy - 2, 2, 2);

          // Outer frame
          ctx.strokeStyle = '#78350f';
          ctx.strokeRect(tx + 2, ty + 2, px - 4, px - 4);
        } else if (pattern.includes('marble') || pattern.includes('spire')) {
          // Iridescent celestial marble with gold filigree veins
          ctx.fillStyle = tile.b;
          ctx.fillRect(tx + 3, ty + 3, px - 6, px - 6);

          // Gold leaf top border
          ctx.fillStyle = tile.accent || '#facc15';
          ctx.fillRect(tx, ty, px, 5);
          ctx.fillStyle = '#fef08a';
          ctx.fillRect(tx + 4, ty + 1, px - 8, 2);

          // Celestial fluted pillar grooves
          ctx.fillStyle = 'rgba(148, 163, 184, 0.4)';
          ctx.fillRect(tx + 12, ty + 8, 6, px - 16);
          ctx.fillRect(tx + 28, ty + 8, 6, px - 16);
          ctx.fillRect(tx + 44, ty + 8, 6, px - 16);

          ctx.strokeStyle = 'rgba(250, 204, 21, 0.4)';
          ctx.strokeRect(tx + 2, ty + 2, px - 4, px - 4);
        } else if (pattern.includes('wood')) {
          // Polished wood scaffolding planks with iron nails
          ctx.fillStyle = tile.b;
          ctx.fillRect(tx, ty + 8, px, px - 16);

          // Wood grain streaks
          ctx.fillStyle = '#78350f';
          ctx.fillRect(tx, ty + 8, px, 3);
          ctx.fillRect(tx, ty + px - 11, px, 3);
          ctx.fillRect(tx + 14, ty + 18, px - 28, 2);

          // Top highlight
          ctx.fillStyle = tile.accent || '#d97706';
          ctx.fillRect(tx, ty + 9, px, 2);

          // Iron nail heads
          ctx.fillStyle = '#18181b';
          ctx.fillRect(tx + 8, ty + px / 2 - 2, 4, 4);
          ctx.fillRect(tx + px - 12, ty + px / 2 - 2, 4, 4);
        }

        // Gameplay semantic decoration
        if (tile.kind === 'hazard') {
          if (tile.id.includes('acid') || tile.id.includes('puddle')) {
            // Bubbling toxic acid vat
            ctx.fillStyle = '#7e22ce';
            ctx.fillRect(tx, ty + 16, px, px - 16);
            ctx.fillStyle = '#a855f7';
            ctx.fillRect(tx, ty + 18, px, px - 18);

            // Acid surface froth & bubbles
            ctx.fillStyle = '#d8b4fe';
            ctx.beginPath();
            ctx.arc(tx + 14, ty + 24, 7, 0, Math.PI * 2);
            ctx.arc(tx + 42, ty + 28, 9, 0, Math.PI * 2);
            ctx.arc(tx + 28, ty + 46, 5, 0, Math.PI * 2);
            ctx.fill();

            // Specular bubble highlights
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(tx + 12, ty + 21, 2, 2);
            ctx.fillRect(tx + 40, ty + 24, 3, 3);
          } else if (tile.id.includes('steam')) {
            // High-pressure steam vent
            ctx.fillStyle = '#fb7185';
            ctx.beginPath();
            ctx.moveTo(tx + px / 2, ty + 6);
            ctx.lineTo(tx + px - 10, ty + px - 6);
            ctx.lineTo(tx + 10, ty + px - 6);
            ctx.closePath();
            ctx.fill();

            // Vent nozzle core
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(tx + px / 2 - 4, ty + 12, 8, 22);
            ctx.fillStyle = '#fda4af';
            ctx.fillRect(tx + px / 2 - 2, ty + 6, 4, 8);
          } else if (tile.id.includes('pendulum')) {
            // Pendulum scythe blade with razor edge
            ctx.fillStyle = '#e11d48';
            ctx.beginPath();
            ctx.arc(tx + px / 2, ty + 10, px / 2 - 6, 0, Math.PI);
            ctx.fill();

            // Gleaming crescent razor edge
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(tx + 8, ty + 10, px - 16, 4);
            ctx.fillRect(tx + px / 2 - 3, ty + 10, 6, px - 20);
          } else {
            // Triple razor iron spikes with edge glints
            ctx.fillStyle = '#ef4444';
            for (let i = 0; i < 3; i++) {
              const sx = tx + i * 20 + 3;
              ctx.beginPath();
              ctx.moveTo(sx + 9, ty + 4);
              ctx.lineTo(sx + 18, ty + px - 4);
              ctx.lineTo(sx, ty + px - 4);
              ctx.closePath();
              ctx.fill();

              // Razor edge shine
              ctx.fillStyle = '#fee2e2';
              ctx.fillRect(sx + 8, ty + 8, 3, 8);
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(sx + 9, ty + 4, 2, 4);
              ctx.fillStyle = '#ef4444';
            }
          }
        } else if (tile.kind === 'checkpoint') {
          // Ornate checkpoint lantern with radiant glowing core
          const glowColor = tile.id.includes('ghost')
            ? '#38bdf8'
            : tile.id.includes('moon')
            ? '#4ade80'
            : '#fbbf24';

          // Lantern cast-iron frame
          ctx.fillStyle = '#18181b';
          ctx.fillRect(tx + 16, ty + 14, px - 32, px - 26);
          ctx.fillStyle = '#27272a';
          ctx.strokeRect(tx + 16, ty + 14, px - 32, px - 26);

          // Radiant illuminated glass orb
          const outerRadius = Math.max(4, px / 2 - 4);
          const orbGrad = ctx.createRadialGradient(
            tx + px / 2,
            ty + px / 2,
            2,
            tx + px / 2,
            ty + px / 2,
            outerRadius,
          );
          orbGrad.addColorStop(0, '#ffffff');
          orbGrad.addColorStop(0.5, glowColor);
          orbGrad.addColorStop(1, 'rgba(0, 0, 0, 0.4)');
          ctx.fillStyle = orbGrad;
          ctx.fillRect(tx + 20, ty + 18, px - 40, px - 36);

          // Lantern pagoda cap & suspension chain
          ctx.fillStyle = '#71717a';
          ctx.fillRect(tx + 12, ty + 10, px - 24, 6);
          ctx.fillRect(tx + px / 2 - 2, ty, 4, 10);
        } else if (tile.kind === 'crumble') {
          // Fractured crumbling roof tile
          ctx.fillStyle = '#d97706';
          ctx.fillRect(tx + 4, ty + 4, px - 8, 12);
          ctx.fillStyle = '#78350f';
          ctx.fillRect(tx + 16, ty + 16, 8, 8);
          ctx.fillRect(tx + 38, ty + 24, 6, 6);
        } else if (tile.kind === 'phase-barrier') {
          // Pulsing purple phase ward lattice
          ctx.fillStyle = 'rgba(168, 85, 247, 0.45)';
          ctx.fillRect(tx, ty, px, px);
          ctx.fillStyle = '#c084fc';
          for (let py = ty + 8; py < ty + px; py += 12) {
            ctx.fillRect(tx + 4, py, px - 8, 3);
          }
          // Glowing node points
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(tx + 8, ty + 8, 4, 4);
          ctx.fillRect(tx + px - 12, ty + px - 12, 4, 4);
        } else if (tile.kind === 'conveyor-left') {
          // Animated conveyor left with neon chevrons
          ctx.fillStyle = '#0284c7';
          ctx.fillRect(tx, ty, px, px);
          ctx.fillStyle = '#bae6fd';
          ctx.beginPath();
          ctx.moveTo(tx + 46, ty + 16);
          ctx.lineTo(tx + 18, ty + px / 2);
          ctx.lineTo(tx + 46, ty + px - 16);
          ctx.fill();
        } else if (tile.kind === 'conveyor-right') {
          // Animated conveyor right with neon chevrons
          ctx.fillStyle = '#0284c7';
          ctx.fillRect(tx, ty, px, px);
          ctx.fillStyle = '#bae6fd';
          ctx.beginPath();
          ctx.moveTo(tx + 18, ty + 16);
          ctx.lineTo(tx + 46, ty + px / 2);
          ctx.lineTo(tx + 18, ty + px - 16);
          ctx.fill();
        } else if (tile.id === 'bell_goal') {
          // Grand golden victory cathedral bell
          const cx = tx + px / 2;
          ctx.fillStyle = '#facc15';
          ctx.beginPath();
          ctx.arc(cx, ty + 24, 20, Math.PI, 0, false);
          ctx.lineTo(tx + px - 8, ty + px - 12);
          ctx.lineTo(tx + 8, ty + px - 12);
          ctx.closePath();
          ctx.fill();

          // Golden bell rim & crown
          ctx.fillStyle = '#eab308';
          ctx.fillRect(tx + 6, ty + px - 14, px - 12, 8);
          ctx.fillStyle = '#fef08a';
          ctx.fillRect(cx - 5, ty + 8, 10, 8);
          ctx.fillRect(cx - 3, ty + 18, 6, 12); // Bell highlight

          // Hanging bell clapper
          ctx.fillStyle = '#ca8a04';
          ctx.beginPath();
          ctx.arc(cx, ty + px - 6, 6, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
  };

  if (map.backgroundCells) {
    renderLayer(map.backgroundCells, true);
  }
  renderLayer(map.cells, false);

  return canvas;
}


function drawForegroundTilemap(
  map: Tilemap2D,
  region: ChunkRegion = { x0: 0, y0: 0, w: map.width, h: map.height },
): HTMLCanvasElement | null {
  if (!map.foregroundCells) return null;
  let any = false;
  for (let y = region.y0; y < region.y0 + region.h && !any; y++) {
    for (let x = region.x0; x < region.x0 + region.w; x++) {
      if (map.foregroundCells[y * map.width + x]) {
        any = true;
        break;
      }
    }
  }
  if (!any) return null;
  const px = 32;
  const canvas = document.createElement('canvas');
  canvas.width = region.w * px;
  canvas.height = region.h * px;
  const ctx = canvas.getContext('2d')!;
  const byId = new Map(map.tiles.map((t) => [t.id, t]));

  for (let y = region.y0; y < region.y0 + region.h; y++) {
    for (let x = region.x0; x < region.x0 + region.w; x++) {
      const tileId = map.foregroundCells[y * map.width + x];
      if (!tileId) continue;
      const tile = byId.get(tileId);
      if (!tile) continue;

      const tx = (x - region.x0) * px;
      const ty = (y - region.y0) * px;

      if (tile.art) {
        paintSpriteCellsAt(ctx, tile.art.size, tile.art.cells, tx, ty, px / tile.art.size);
        continue;
      }

      ctx.fillStyle = tile.a;
      ctx.fillRect(tx, ty, px, px);
      if (tile.accent) {
        ctx.fillStyle = tile.accent;
        ctx.fillRect(tx, ty, px, 4);
      }
    }
  }
  return canvas;
}

/** Split the map into CHUNK_TILES square regions, rasterising each into its
 * own canvas texture. Chunks are shared per map through `chunkCache` and
 * disposed by the last layer component to unmount. */
function buildChunks(map: Tilemap2D): { main: TileChunk[]; fg: TileChunk[] } {
  const cached = chunkCache.get(map);
  if (cached) return cached;
  if (map.width * map.height > MAX_MAP_CELLS) {
    throw new Error(
      `Platformer2DScene: map is ${map.width} by ${map.height} tiles (${map.width * map.height} cells); the renderer supports at most ${MAX_MAP_CELLS} cells`,
    );
  }
  const main: TileChunk[] = [];
  const fg: TileChunk[] = [];
  for (let y0 = 0; y0 < map.height; y0 += CHUNK_TILES) {
    for (let x0 = 0; x0 < map.width; x0 += CHUNK_TILES) {
      const region: ChunkRegion = { x0, y0, w: Math.min(CHUNK_TILES, map.width - x0), h: Math.min(CHUNK_TILES, map.height - y0) };
      main.push({ region, texture: canvasTexture(drawTilemap(map, region)) });
      const fgCanvas = drawForegroundTilemap(map, region);
      fg.push({ region, texture: fgCanvas ? canvasTexture(fgCanvas) : null });
    }
  }
  const built = { main, fg };
  chunkCache.set(map, built);
  return built;
}

function disposeChunks(map: Tilemap2D): void {
  const built = chunkCache.get(map);
  if (!built) return;
  for (const c of built.main) c.texture?.dispose();
  for (const c of built.fg) c.texture?.dispose();
  chunkCache.delete(map);
}

/** Orthographic view bounds in tile space (x right, y down), with a margin. */
function cameraTileBounds(camera: THREE.Camera, margin = CHUNK_TILES): { minX: number; maxX: number; minY: number; maxY: number } {
  const cam = camera as THREE.OrthographicCamera;
  const zoom = 'zoom' in cam && typeof cam.zoom === 'number' ? cam.zoom : PIXELS_PER_TILE;
  const halfW = (cam.right - cam.left) / 2 / zoom;
  const halfH = (cam.top - cam.bottom) / 2 / zoom;
  const cx = camera.position.x;
  const cy = -camera.position.y;
  return { minX: cx - halfW - margin, maxX: cx + halfW + margin, minY: cy - halfH - margin, maxY: cy + halfH + margin };
}

/** One tile layer of the map as culled chunk planes. `layer` 'main' is the
 * background plus main cells; 'fg' the foreground overlay. */
function ChunkedTilemapLayer({ map, layer, z }: { map: Tilemap2D; layer: 'main' | 'fg'; z: number }) {
  const chunks = useMemo(() => buildChunks(map)[layer], [map, layer]);
  const groupRef = useRef<THREE.Group>(null);
  const { camera } = useThree();

  // Dispose when this map's last layer unmounts (both layers share the cache).
  useEffect(() => {
    return () => {
      // Defer so the sibling layer unmounting in the same commit can still
      // read the cache; the second call is a no-op.
      const target = map;
      queueMicrotask(() => disposeChunks(target));
    };
  }, [map]);

  useFrame(() => {
    const group = groupRef.current;
    if (!group) return;
    const b = cameraTileBounds(camera);
    for (let i = 0; i < group.children.length; i++) {
      const mesh = group.children[i] as THREE.Mesh;
      const region = chunks[i]?.region;
      if (!region) continue;
      const visible = region.x0 + region.w >= b.minX && region.x0 <= b.maxX && region.y0 + region.h >= b.minY && region.y0 <= b.maxY;
      mesh.visible = visible;
    }
  });

  return (
    <group ref={groupRef}>
      {chunks.map((chunk, i) => (
        <mesh
          key={i}
          position={[chunk.region.x0 + chunk.region.w / 2, -(chunk.region.y0 + chunk.region.h / 2), z]}
          visible={Boolean(chunk.texture)}
        >
          <planeGeometry args={[chunk.region.w * TILE_SIZE, chunk.region.h * TILE_SIZE]} />
          {chunk.texture ? (
            <meshBasicMaterial map={chunk.texture} transparent toneMapped={false} />
          ) : (
            <meshBasicMaterial transparent opacity={0} />
          )}
        </mesh>
      ))}
    </group>
  );
}

/** One dynamic entity's mesh. A baked `art` sprite (see `DynamicEntity.art`)
 * is painted as a textured plane in place of the built-in flat-colour
 * placeholder; absent, every entity keeps rendering exactly as it did.
 * Split out from `DynamicEntitiesMesh` so each entity gets its own `useMemo`
 * for its own texture - hooks cannot be called conditionally inside a single
 * shared component's `.map()` callback. */
function DynamicEntityMesh({ ent, stateRef }: { ent: DynamicEntity; stateRef: React.MutableRefObject<PlatformerState | null> }) {
  const w = ent.width || (ent.type === 'steam-vent' ? 1.5 : ent.type === 'moving-hazard' ? 1 : 3);
  const h = ent.height || (ent.type === 'steam-vent' ? 6 : ent.type === 'moving-hazard' ? 1 : 0.6);
  const meshRef = useRef<THREE.Mesh>(null);
  useFrame(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const elapsed = stateRef.current?.elapsed ?? 0;
    const pos = dynamicEntityPosition(ent, elapsed);
    mesh.position.x = pos.x;
    mesh.position.y = -pos.y;
  });

  // A baked sprite is always a square (Sprite2D has one `size`, never a
  // separate width/height), but an entity's own plane rarely is - a
  // steam-vent can be 1.5 x 11 tiles. Stretching one square texture over
  // that would squash it into an unrecognisable smear, so instead this
  // repeats the square motif across the plane at true 1-tile scale (one
  // repeat per tile of width/height), the same "author one tileable unit"
  // approach the tilemap's own tile art already relies on, just applied to
  // a moving prop instead of a static grid cell.
  const texture = useMemo(() => {
    if (!ent.art) return null;
    const tex = canvasTexture(drawSprite(ent.art));
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(w, h);
    return tex;
  }, [ent.art, w, h]);
  useEffect(() => () => texture?.dispose(), [texture]);

  if (ent.type === 'moving-platform' || ent.type === 'moving-hazard') {
    return (
      <mesh ref={meshRef} position={[ent.x, -ent.y, 0]}>
        <planeGeometry args={[w, h]} />
        {texture ? (
          <meshBasicMaterial map={texture} transparent toneMapped={false} />
        ) : (
          <meshBasicMaterial color={ent.type === 'moving-hazard' ? '#e11d48' : '#b45309'} toneMapped={false} />
        )}
      </mesh>
    );
  } else if (ent.type === 'steam-vent') {
    return (
      <mesh position={[ent.x, -(ent.y - h / 2), -0.01]}>
        <planeGeometry args={[w, h]} />
        {texture ? (
          <meshBasicMaterial map={texture} transparent toneMapped={false} />
        ) : (
          <meshBasicMaterial color="#fda4af" transparent opacity={0.35} toneMapped={false} />
        )}
      </mesh>
    );
  }
  return null;
}

function DynamicEntitiesMesh({
  dynamicEntities = [],
  stateRef,
}: {
  dynamicEntities?: DynamicEntity[];
  stateRef: React.MutableRefObject<PlatformerState | null>;
}) {
  const props = dynamicEntities.filter((e) => e.type !== 'enemy' && e.type !== 'pickup' && e.type !== 'patrol-bird');
  if (props.length === 0) return null;

  return (
    <group position={[0, 0, 0.08]}>
      {props.map((ent) => (
        <DynamicEntityMesh key={ent.id} ent={ent} stateRef={stateRef} />
      ))}
    </group>
  );
}

/**
 * How far (in tiles, upward) to lift a sprite quad of `quadHeight` tiles so
 * its `anchorY` feet row sits on the bottom of a collision box of
 * `halfHeight`. Zero when the sprite has no anchor (centred, as before).
 */
export function spriteAnchorLift(sprite: Sprite2D | undefined, quadHeight: number, halfHeight: number): number {
  if (!sprite || sprite.anchorY === undefined) return 0;
  const feetFromCentre = (sprite.anchorY - 0.5) * quadHeight;
  return feetFromCentre - halfHeight;
}

/** Pick the animation clip for an enemy from its live state. */
function enemyClipFor(e: EnemyRuntime, sprite: Sprite2D): string {
  const wanted =
    e.state === 'move'
      ? 'move'
      : e.state === 'telegraph'
        ? 'telegraph'
        : e.state === 'attack'
          ? 'attack'
          : e.state === 'hurt' || e.state === 'stunned'
            ? 'hurt'
            : e.state === 'dead'
              ? 'death'
              : 'idle';
  if (sprite.animations?.[wanted]) return wanted;
  if (sprite.animations?.idle) return 'idle';
  return wanted;
}

/** Elapsed seconds into the current clip: enemies restart their clip on
 * every state change, tracked per mesh through a small ref. */
function EnemyMesh({ ent, stateRef }: { ent: DynamicEntity; stateRef: React.MutableRefObject<PlatformerState | null> }) {
  const def = ent.enemy!;
  const w = Math.max(1, Math.ceil(def.halfWidth * 2));
  const h = Math.max(1, Math.ceil(def.halfHeight * 2));
  const meshRef = useRef<THREE.Mesh>(null);
  const clipRef = useRef<{ name: string; elapsed: number; lastState: string }>({ name: 'idle', elapsed: 0, lastState: 'idle' });
  const flipbook = useMemo(() => {
    if (!ent.art || !ent.art.cells.some((c) => c !== null)) return null;
    const canvas = drawSprite(ent.art);
    return { sprite: ent.art, canvas, texture: canvasTexture(canvas) };
  }, [ent.art]);
  useEffect(() => () => flipbook?.texture.dispose(), [flipbook]);

  useFrame((_, dt) => {
    const mesh = meshRef.current;
    const e = stateRef.current?.enemies[ent.id];
    if (!mesh || !e) return;
    if (e.state === 'dead' && e.deathTimer >= ENEMY_DEATH_HOLD_SECONDS) {
      mesh.visible = false;
      return;
    }
    mesh.visible = true;
    mesh.position.set(e.x, -e.y + spriteAnchorLift(ent.art, h, def.halfHeight), 0.08);
    const facingSign = e.facing === 'left' ? -1 : 1;
    mesh.scale.set(facingSign * w, h, 1);
    if (flipbook) {
      const clip = enemyClipFor(e, flipbook.sprite);
      const c = clipRef.current;
      if (c.name !== clip || c.lastState !== e.state) {
        c.name = clip;
        c.lastState = e.state;
        c.elapsed = 0;
      } else {
        c.elapsed += dt;
      }
      drawSpriteCells(flipbook.canvas, flipbook.sprite.size, spriteAnimationCells(flipbook.sprite, clip, c.elapsed));
      flipbook.texture.needsUpdate = true;
    }
    const mat = mesh.material as THREE.MeshBasicMaterial;
    if (e.hurtFlash > 0) {
      mat.color.setRGB(3, 3, 3);
    } else {
      mat.color.setRGB(1, 1, 1);
    }
    mat.opacity = e.state === 'dead' ? Math.max(0, 1 - e.deathTimer / ENEMY_DEATH_HOLD_SECONDS) : 1;
  });

  return (
    <mesh ref={meshRef} position={[ent.x, -ent.y, 0.08]}>
      <planeGeometry args={[1, 1]} />
      {flipbook ? (
        <meshBasicMaterial map={flipbook.texture} transparent toneMapped={false} />
      ) : (
        <meshBasicMaterial color="#a21caf" transparent toneMapped={false} />
      )}
    </mesh>
  );
}

function EnemiesLayer({ dynamicEntities = [], stateRef }: { dynamicEntities?: DynamicEntity[]; stateRef: React.MutableRefObject<PlatformerState | null> }) {
  const enemies = dynamicEntities.filter((e) => e.type === 'enemy' && e.enemy);
  if (enemies.length === 0) return null;
  return (
    <group>
      {enemies.map((ent) => (
        <EnemyMesh key={ent.id} ent={ent} stateRef={stateRef} />
      ))}
    </group>
  );
}

function PickupMesh({ ent, stateRef }: { ent: DynamicEntity; stateRef: React.MutableRefObject<PlatformerState | null> }) {
  const meshRef = useRef<THREE.Mesh>(null);
  const texture = useMemo(() => (ent.art ? canvasTexture(drawSprite(ent.art)) : null), [ent.art]);
  useEffect(() => () => texture?.dispose(), [texture]);
  useFrame(({ clock }) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const collected = stateRef.current?.pickupsCollected.includes(ent.id) ?? false;
    mesh.visible = !collected;
    mesh.position.y = -ent.y + Math.sin(clock.elapsedTime * 3 + ent.x) * 0.12;
  });
  const colour = ent.pickup?.kind === 'health' ? '#f87171' : ent.pickup?.kind === 'weapon' ? '#facc15' : '#a78bfa';
  return (
    <mesh ref={meshRef} position={[ent.x, -ent.y, 0.085]}>
      <planeGeometry args={[1, 1]} />
      {texture ? <meshBasicMaterial map={texture} transparent toneMapped={false} /> : <meshBasicMaterial color={colour} toneMapped={false} />}
    </mesh>
  );
}

function PickupsLayer({ dynamicEntities = [], stateRef }: { dynamicEntities?: DynamicEntity[]; stateRef: React.MutableRefObject<PlatformerState | null> }) {
  const pickups = dynamicEntities.filter((e) => e.type === 'pickup' && e.pickup);
  if (pickups.length === 0) return null;
  return (
    <group>
      {pickups.map((ent) => (
        <PickupMesh key={ent.id} ent={ent} stateRef={stateRef} />
      ))}
    </group>
  );
}

/** Projectiles as instanced quads; a projectile with baked art draws it,
 * the rest are coloured squares (player gold, enemy red). */
const MAX_PROJECTILE_MESHES = 64;
function ProjectilesLayer({ stateRef, projectileArt }: { stateRef: React.MutableRefObject<PlatformerState | null>; projectileArt?: Record<string, Sprite2D> }) {
  const groupRef = useRef<THREE.Group>(null);
  const textures = useMemo(() => {
    const out: Record<string, THREE.CanvasTexture> = {};
    for (const [kind, art] of Object.entries(projectileArt ?? {})) out[kind] = canvasTexture(drawSprite(art));
    return out;
  }, [projectileArt]);
  useEffect(() => () => Object.values(textures).forEach((t) => t.dispose()), [textures]);

  useFrame(() => {
    const group = groupRef.current;
    const projectiles: Projectile[] = stateRef.current?.projectiles ?? [];
    if (!group) return;
    for (let i = 0; i < group.children.length; i++) {
      const mesh = group.children[i] as THREE.Mesh;
      const p = projectiles[i];
      if (!p) {
        mesh.visible = false;
        continue;
      }
      mesh.visible = true;
      mesh.position.set(p.x, -p.y, 0.085);
      const size = Math.max(0.35, p.radius * 2.2);
      mesh.scale.set(p.vx < 0 ? -size : size, size, 1);
      const mat = mesh.material as THREE.MeshBasicMaterial;
      const tex = p.spec.kind ? textures[p.spec.kind] : undefined;
      if (tex && mat.map !== tex) {
        mat.map = tex;
        mat.color.setRGB(1, 1, 1);
        mat.needsUpdate = true;
      } else if (!tex) {
        if (mat.map) {
          mat.map = null;
          mat.needsUpdate = true;
        }
        mat.color.set(p.owner === 'player' ? '#facc15' : '#f43f5e');
      }
    }
  });

  return (
    <group ref={groupRef}>
      {Array.from({ length: MAX_PROJECTILE_MESHES }).map((_, i) => (
        <mesh key={i} visible={false} position={[0, 0, 0.085]}>
          <planeGeometry args={[1, 1]} />
          <meshBasicMaterial transparent toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

/** Square particle bursts driven by physics events. */
function ParticlesLayer({ particlesRef }: { particlesRef: React.MutableRefObject<Particle2D[]> }) {
  const groupRef = useRef<THREE.Group>(null);
  const MAX = 256;
  useFrame(() => {
    const group = groupRef.current;
    if (!group) return;
    const list = particlesRef.current;
    for (let i = 0; i < group.children.length; i++) {
      const mesh = group.children[i] as THREE.Mesh;
      const p = list[i];
      if (!p) {
        mesh.visible = false;
        continue;
      }
      mesh.visible = true;
      mesh.position.set(p.x, -p.y, 0.12);
      const k = p.life / p.maxLife;
      mesh.scale.set(p.size * (0.4 + k * 0.6), p.size * (0.4 + k * 0.6), 1);
      const mat = mesh.material as THREE.MeshBasicMaterial;
      mat.color.set(p.colour);
      mat.opacity = Math.min(1, k * 1.5);
    }
  });
  return (
    <group ref={groupRef}>
      {Array.from({ length: MAX }).map((_, i) => (
        <mesh key={i} visible={false} position={[0, 0, 0.12]}>
          <planeGeometry args={[1, 1]} />
          <meshBasicMaterial transparent toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
    </group>
  );
}

function PointLightRadianceMesh({ map }: { map: Tilemap2D }) {
  const lights = useMemo(() => {
    const list: { x: number; y: number; color: string; size: number; opacity: number }[] = [];
    const byId = new Map(map.tiles.map((t) => [t.id, t]));

    for (let y = 0; y < map.height; y++) {
      for (let x = 0; x < map.width; x++) {
        const tileId = map.cells[y * map.width + x];
        if (!tileId) continue;
        const tile = byId.get(tileId);
        if (!tile) continue;

        if (tile.kind === 'checkpoint') {
          list.push({ x: x + 0.5, y: y + 0.5, color: tile.glow ?? '#fbbf24', size: tile.glowSize ?? 5.5, opacity: tile.glowOpacity ?? 0.28 });
        } else if (tile.kind === 'goal') {
          list.push({ x: x + 0.5, y: y + 0.5, color: tile.glow ?? '#facc15', size: tile.glowSize ?? 9.0, opacity: tile.glowOpacity ?? 0.35 });
        } else if (tile.glow && (tile.kind === 'hazard' || tile.glowSize !== undefined)) {
          list.push({ x: x + 0.5, y: y + 0.5, color: tile.glow, size: tile.glowSize ?? 3.5, opacity: tile.glowOpacity ?? 0.15 });
        }
      }
    }
    return list;
  }, [map]);

  const texture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    const grad = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    grad.addColorStop(0, 'rgba(255, 255, 255, 1)');
    grad.addColorStop(0.4, 'rgba(255, 255, 255, 0.4)');
    grad.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 64, 64);
    return canvasTexture(canvas);
  }, []);
  useEffect(() => () => texture.dispose(), [texture]);

  return (
    <group position={[0, 0, 0.05]}>
      {lights.map((l, i) => (
        <mesh key={i} position={[l.x, -l.y, 0]}>
          <planeGeometry args={[l.size, l.size]} />
          <meshBasicMaterial
            map={texture}
            color={l.color}
            transparent
            opacity={l.opacity}
            depthWrite={false}
            toneMapped={false}
          />
        </mesh>
      ))}
    </group>
  );
}

/** Optional baked replacements for one theme's three procedural parallax
 * layers, keyed by texture URL. Any layer left `undefined` keeps this
 * scene's own procedural canvas for that layer - a game can bake just the
 * layers it has art for. Purely additive: omitting `backdropArt` entirely
 * (every existing caller) reproduces the previous procedural-only output
 * exactly. */
export interface BackdropArt {
  far?: string;
  mid?: string;
  weather?: string;
}

/** Chapter-specific multi-plane parallax atmospheric skyboxes. */
function ParallaxBackground({
  mapWidth,
  mapHeight,
  theme = 'rooftops',
  backdropArt,
}: {
  mapWidth: number;
  mapHeight: number;
  theme?: string;
  backdropArt?: BackdropArt;
}) {
  const { camera } = useThree();
  const farRef = useRef<THREE.Mesh>(null);
  const midRef = useRef<THREE.Mesh>(null);
  const weatherRef = useRef<THREE.Mesh>(null);

  const bakedFar = useMemo(() => (backdropArt?.far ? loadBakedTexture(backdropArt.far) : null), [backdropArt]);
  const bakedMid = useMemo(() => (backdropArt?.mid ? loadBakedTexture(backdropArt.mid) : null), [backdropArt]);
  const bakedWeather = useMemo(
    () => (backdropArt?.weather ? loadBakedTexture(backdropArt.weather) : null),
    [backdropArt],
  );

  // Far sky canvas (moon, stars, gradients)
  const farTexture = useMemo(() => {
    if (parallaxFarCache.has(theme)) return parallaxFarCache.get(theme)!;
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;

    const grad = ctx.createLinearGradient(0, 0, 0, 256);

    if (theme === 'foundry') {
      grad.addColorStop(0, '#0c0a09');
      grad.addColorStop(0.5, '#1c1917');
      grad.addColorStop(1, '#451a03'); // fiery furnace glow
    } else if (theme === 'haunted') {
      grad.addColorStop(0, '#030206');
      grad.addColorStop(0.5, '#1e1b4b'); // deep gothic indigo
      grad.addColorStop(1, '#2e1065');
    } else if (theme === 'clocktower') {
      grad.addColorStop(0, '#18120c');
      grad.addColorStop(0.5, '#292524');
      grad.addColorStop(1, '#451a03'); // warm horological amber
    } else if (theme === 'moonlit') {
      grad.addColorStop(0, '#022c22');
      grad.addColorStop(0.4, '#064e3b');
      grad.addColorStop(0.8, '#065f46');
      grad.addColorStop(1, '#022c22'); // emerald celestial horizon
    } else {
      // rooftops (Victorian midnight)
      grad.addColorStop(0, '#040407');
      grad.addColorStop(0.5, '#0b0f19');
      grad.addColorStop(1, '#111827');
    }

    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 512, 256);

    // Glowing Moon with Atmospheric Halo
    const moonX = 410;
    const moonY = 55;
    const moonHalo = ctx.createRadialGradient(moonX, moonY, 5, moonX, moonY, 70);
    moonHalo.addColorStop(0, 'rgba(254, 240, 138, 0.45)');
    moonHalo.addColorStop(0.5, 'rgba(250, 204, 21, 0.15)');
    moonHalo.addColorStop(1, 'rgba(250, 204, 21, 0)');
    ctx.fillStyle = moonHalo;
    ctx.beginPath();
    ctx.arc(moonX, moonY, 70, 0, Math.PI * 2);
    ctx.fill();

    // Radiant Moon Disc
    ctx.fillStyle = '#fef08a';
    ctx.beginPath();
    ctx.arc(moonX, moonY, 24, 0, Math.PI * 2);
    ctx.fill();

    // Crescent shadow cutout
    ctx.fillStyle = theme === 'foundry' ? '#1c1917' : theme === 'haunted' ? '#1e1b4b' : theme === 'moonlit' ? '#064e3b' : '#0b0f19';
    ctx.beginPath();
    ctx.arc(moonX - 9, moonY - 5, 21, 0, Math.PI * 2);
    ctx.fill();

    // Twinkling stars & celestial nebula dust
    for (let i = 0; i < 60; i++) {
      const sx = (i * 137) % 512;
      const sy = (i * 79) % 190;
      const sz = i % 4 === 0 ? 2 : 1;
      const alpha = 0.4 + ((i * 17) % 60) / 100;
      ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
      ctx.fillRect(sx, sy, sz, sz);
    }
    const tex = canvasTexture(canvas);
    parallaxFarCache.set(theme, tex);
    return tex;
  }, [theme]);

  // Mid skyline canvas (chapter silhouetted architecture & monuments)
  const midTexture = useMemo(() => {
    if (parallaxMidCache.has(theme)) return parallaxMidCache.get(theme)!;
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 128;
    const ctx = canvas.getContext('2d')!;

    if (theme === 'foundry') {
      // Industrial smokestacks, blast furnaces & gantry cranes
      ctx.fillStyle = 'rgba(28, 25, 23, 0.92)';
      for (let x = 0; x < 512; x += 32) {
        const h = 35 + ((x * 29) % 55);
        ctx.fillRect(x, 128 - h, 24, h);
      }
    } else if (theme === 'haunted') {
      // Gothic cathedral spires, pointed arches & graveyard crosses
      ctx.fillStyle = 'rgba(15, 23, 42, 0.94)';
      for (let x = 0; x < 512; x += 28) {
        const h = 30 + ((x * 37) % 60);
        ctx.fillRect(x, 128 - h, 22, h);
        ctx.beginPath();
        ctx.moveTo(x + 11, 128 - h - 22);
        ctx.lineTo(x + 22, 128 - h);
        ctx.lineTo(x, 128 - h);
        ctx.fill();
      }
    } else if (theme === 'clocktower') {
      // Massive rotating horology gears & clock faces
      ctx.fillStyle = 'rgba(69, 26, 3, 0.88)';
      for (let x = 30; x < 512; x += 80) {
        ctx.beginPath();
        ctx.arc(x, 80, 40, 0, Math.PI * 2);
        ctx.fill();
      }
    } else if (theme === 'moonlit') {
      // Soaring celestial palace spires
      ctx.fillStyle = 'rgba(6, 78, 59, 0.9)';
      for (let x = 0; x < 512; x += 26) {
        const h = 40 + ((x * 43) % 55);
        ctx.fillRect(x, 128 - h, 20, h);
        ctx.beginPath();
        ctx.moveTo(x + 10, 128 - h - 25);
        ctx.lineTo(x + 20, 128 - h);
        ctx.lineTo(x, 128 - h);
        ctx.fill();
      }
    } else {
      // Rooftops default: Victorian chimney stacks & pitched roofs with warm windows
      ctx.fillStyle = 'rgba(15, 23, 42, 0.88)';
      for (let x = 0; x < 512; x += 24) {
        const h = 25 + ((x * 23) % 45);
        ctx.fillRect(x, 128 - h, 21, h);
        ctx.beginPath();
        ctx.moveTo(x + 10, 128 - h - 15);
        ctx.lineTo(x + 21, 128 - h);
        ctx.lineTo(x, 128 - h);
        ctx.fill();
      }
    }
    const tex = canvasTexture(canvas);
    parallaxMidCache.set(theme, tex);
    return tex;
  }, [theme]);

  // Weather overlay canvas (rain streaks, rising embers, graveyard fog)
  const weatherTexture = useMemo(() => {
    if (parallaxWeatherCache.has(theme)) return parallaxWeatherCache.get(theme)!;
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;

    if (theme === 'rooftops') {
      // Diagonal rain streaks with landing ripples
      ctx.strokeStyle = 'rgba(186, 230, 253, 0.35)';
      ctx.lineWidth = 1.5;
      for (let i = 0; i < 40; i++) {
        const rx = (i * 61) % 256;
        const ry = (i * 47) % 256;
        ctx.beginPath();
        ctx.moveTo(rx, ry);
        ctx.lineTo(rx - 7, ry + 14);
        ctx.stroke();
      }
    } else if (theme === 'foundry') {
      // Rising glowing furnace embers & sparks
      for (let i = 0; i < 30; i++) {
        const rx = (i * 67) % 256;
        const ry = (i * 53) % 256;
        ctx.fillStyle = i % 2 === 0 ? 'rgba(251, 146, 60, 0.6)' : 'rgba(254, 240, 138, 0.7)';
        ctx.fillRect(rx, ry, 2, 2);
      }
    } else if (theme === 'haunted') {
      // Volumetric graveyard fog
      const grad = ctx.createLinearGradient(0, 180, 0, 256);
      grad.addColorStop(0, 'rgba(192, 132, 252, 0)');
      grad.addColorStop(1, 'rgba(192, 132, 252, 0.28)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 180, 256, 76);
    } else if (theme === 'clocktower') {
      // Floating golden horological dust motes
      for (let i = 0; i < 25; i++) {
        const rx = (i * 73) % 256;
        const ry = (i * 59) % 256;
        ctx.fillStyle = 'rgba(251, 191, 36, 0.45)';
        ctx.beginPath();
        ctx.arc(rx, ry, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
    } else if (theme === 'moonlit') {
      // Shimmering celestial starburst particles
      for (let i = 0; i < 25; i++) {
        const rx = (i * 83) % 256;
        const ry = (i * 67) % 256;
        ctx.fillStyle = 'rgba(74, 222, 128, 0.4)';
        ctx.fillRect(rx, ry, 1.5, 1.5);
      }
    }
    const tex = canvasTexture(canvas);
    parallaxWeatherCache.set(theme, tex);
    return tex;
  }, [theme]);

  useFrame(() => {
    if (farRef.current) {
      farRef.current.position.x = camera.position.x * 0.88 + mapWidth * 0.08;
      farRef.current.position.y = camera.position.y * 0.88 - mapHeight * 0.08;
    }
    if (midRef.current) {
      midRef.current.position.x = camera.position.x * 0.55 + mapWidth * 0.2;
      midRef.current.position.y = camera.position.y * 0.55 - mapHeight * 0.25;
    }
    if (weatherRef.current) {
      weatherRef.current.position.x = camera.position.x;
      weatherRef.current.position.y = camera.position.y;
    }
  });

  return (
    <group position={[0, 0, -2]}>
      {/* Far Background Sky */}
      <mesh ref={farRef} position={[mapWidth / 2, -mapHeight / 2, -2]}>
        <planeGeometry args={[mapWidth * 2.0, mapHeight * 2.0]} />
        <meshBasicMaterial map={bakedFar ?? farTexture} toneMapped={false} />
      </mesh>
      {/* Mid Background Skyline */}
      <mesh ref={midRef} position={[mapWidth / 2, -mapHeight * 0.7, -1]}>
        <planeGeometry args={[mapWidth * 1.6, mapHeight * 0.9]} />
        <meshBasicMaterial map={bakedMid ?? midTexture} transparent toneMapped={false} />
      </mesh>
      {/* Weather Overlay */}
      <mesh ref={weatherRef} position={[mapWidth / 2, -mapHeight / 2, 0.05]}>
        <planeGeometry args={[mapWidth * 1.3, mapHeight * 1.3]} />
        <meshBasicMaterial map={bakedWeather ?? weatherTexture} transparent opacity={0.7} toneMapped={false} />
      </mesh>
    </group>
  );
}

function pollGamepadState(prevButtons: React.MutableRefObject<boolean[]>, bindings: PlatformerBindings) {
  const gp = getPrimaryGamepad();
  if (!gp) return null;

  const stickX = Math.abs(gp.axes[0] ?? 0) > 0.2 ? (gp.axes[0] ?? 0) : 0;
  const isDown = (idx: number) => Boolean(gp.buttons[idx]?.pressed || (gp.buttons[idx]?.value ?? 0) > 0.3);
  const wasDown = (idx: number) => Boolean(prevButtons.current[idx]);
  const anyDown = (list: number[]) => list.some(isDown);
  const anyJust = (list: number[]) => list.some((i) => isDown(i) && !wasDown(i));

  let moveX = stickX;
  if (anyDown(bindings.gamepad.right)) moveX = 1;
  else if (anyDown(bindings.gamepad.left)) moveX = -1;

  const result = {
    moveX,
    jumpHeld: anyDown(bindings.gamepad.jump),
    jumpPressed: anyJust(bindings.gamepad.jump),
    dashPressed: anyJust(bindings.gamepad.dash),
    teleportPressed: anyJust(bindings.gamepad.teleport),
    shadowFormPressed: anyJust(bindings.gamepad.shadowForm),
    rewindHeld: anyDown(bindings.gamepad.rewind),
    ghostDoublePressed: anyJust(bindings.gamepad.ghostDouble),
    attackPressed: anyJust(bindings.gamepad.attack),
    attackHeld: anyDown(bindings.gamepad.attack),
  };

  const currentButtonStates: boolean[] = [];
  for (let i = 0; i < gp.buttons.length; i++) currentButtonStates[i] = isDown(i);
  prevButtons.current = currentButtonStates;
  return result;
}

function triggerGamepadHaptics(effect: { duration: number; strongMagnitude: number; weakMagnitude: number }): void {
  for (const gp of getConnectedGamepads()) {
    const actuator = (gp as unknown as { vibrationActuator?: { playEffect?: (type: string, params: Record<string, unknown>) => Promise<string> } }).vibrationActuator;
    if (actuator && typeof actuator.playEffect === 'function') {
      try {
        actuator.playEffect('dual-rumble', {
          startDelay: 0,
          duration: effect.duration,
          strongMagnitude: effect.strongMagnitude,
          weakMagnitude: effect.weakMagnitude,
        });
      } catch {
        // Ignore haptics permission or platform quirks
      }
    }
  }
}

const NEUTRAL_INPUT: PlatformerInput = {
  moveX: 0,
  jumpPressed: false,
  jumpHeld: false,
  dashPressed: false,
  teleportPressed: false,
  shadowFormPressed: false,
  rewindHeld: false,
  ghostDoublePressed: false,
  attackPressed: false,
  attackHeld: false,
};

function readInput(bindings: PlatformerBindings, prevGamepadButtons: React.MutableRefObject<boolean[]>): PlatformerInput {
  const typing = isTypingIntoFormField();
  const anyDown = (list: string[]) => !typing && list.some(isKeyDown);
  const anyJust = (list: string[]) => !typing && list.some(isKeyJustPressed);
  const gp = pollGamepadState(prevGamepadButtons, bindings);

  let moveX = (anyDown(bindings.keyboard.right) ? 1 : 0) - (anyDown(bindings.keyboard.left) ? 1 : 0);
  if (gp && Math.abs(gp.moveX) > 0.1) moveX = gp.moveX > 0 ? 1 : -1;

  const input: PlatformerInput = {
    moveX,
    jumpPressed: anyJust(bindings.keyboard.jump) || Boolean(gp?.jumpPressed),
    jumpHeld: anyDown(bindings.keyboard.jump) || Boolean(gp?.jumpHeld),
    dashPressed: anyJust(bindings.keyboard.dash) || Boolean(gp?.dashPressed),
    teleportPressed: anyJust(bindings.keyboard.teleport) || Boolean(gp?.teleportPressed),
    shadowFormPressed: anyJust(bindings.keyboard.shadowForm) || Boolean(gp?.shadowFormPressed),
    rewindHeld: anyDown(bindings.keyboard.rewind) || Boolean(gp?.rewindHeld),
    ghostDoublePressed: anyJust(bindings.keyboard.ghostDouble) || Boolean(gp?.ghostDoublePressed),
    attackPressed: anyJust(bindings.keyboard.attack) || Boolean(gp?.attackPressed),
    attackHeld: anyDown(bindings.keyboard.attack) || Boolean(gp?.attackHeld),
  };
  clearJustPressedKeys();
  return input;
}

interface PlatformerPlayerProps {
  map: Tilemap2D;
  abilities?: PlatformerAbilities;
  config?: PlatformerConfig;
  dynamicEntities?: DynamicEntity[];
  follow?: boolean;
  /** Zeroes every input flag before it ever reaches `stepPlatformer`;
   * physics (gravity, momentum, landing) still runs. */
  movementLocked?: boolean;
  weapon?: WeaponSpec | null;
  bindings: PlatformerBindings;
  /** Freezes the simulation (no physics steps, no particle updates). */
  paused?: boolean;
  /** Simulation speed multiplier; 1 is real time. */
  timeScale?: number;
  stateRef: React.MutableRefObject<PlatformerState | null>;
  particlesRef: React.MutableRefObject<Particle2D[]>;
  particlesEnabled: boolean;
  /** Colour for hit sparks, usually the weapon's accent. */
  /** Resting camera zoom in pixels per tile; see Platformer2DSceneProps. */
  cameraZoom?: number;
  hitColour?: string;
  onPlayerStateChange?: (state: PlatformerState) => void;
  onEvent?: (event: PlatformerEvent) => void;
}

/** Player pose from physics state. Prefers `attack-<weaponId>` clips. */
function selectPose(state: PlatformerState, input: PlatformerInput, isDead: boolean, sprite: Sprite2D | undefined): string {
  const has = (name: string) => Boolean(sprite?.animations?.[name]);
  if (isDead || state.isDead) return 'death-pancake';
  if (state.hurtTimer > 0 && has('hurt')) return 'hurt';
  if (state.attack) {
    const specific = `attack-${state.attack.weaponId}`;
    if (has(specific)) return specific;
    if (has('attack')) return 'attack';
  }
  if (state.verticalState === 'dashing' || state.dashTimer > 0) return 'dash';
  if (state.verticalState === 'rewinding') return 'rewind';
  if (state.shadowFormTimer > 0) return 'shadow-form';
  if (state.verticalState === 'wall-sliding') return 'wall-slide';
  if (state.verticalState === 'gliding' && has('glide')) return 'glide';
  if (state.verticalState === 'jumping') return 'jump';
  if (state.verticalState === 'falling' || state.verticalState === 'gliding') return 'fall';
  if (state.isGrounded && Math.abs(state.vx) > 1.5 && input.moveX !== 0 && Math.sign(input.moveX) !== Math.sign(state.vx)) return 'skid';
  if (Math.abs(state.vx) > 0.8) return 'run';
  return 'idle';
}

function PlatformerPlayer({
  map,
  abilities,
  config = DEFAULT_PLATFORMER_CONFIG,
  dynamicEntities = [],
  follow = true,
  movementLocked = false,
  weapon = null,
  bindings,
  paused = false,
  timeScale = 1,
  stateRef,
  particlesRef,
  particlesEnabled,
  hitColour,
  cameraZoom = PIXELS_PER_TILE,
  onPlayerStateChange,
  onEvent,
}: PlatformerPlayerProps) {
  const grid = useMemo(() => buildPlatformerGrid(map), [map]);
  const spawn = useMemo(() => {
    if (map.spawn) return map.spawn;
    return { x: 2.0, y: Math.max(1.0, map.height - 3.0) };
  }, [map]);

  const physicsRef = useRef<{ prev: PlatformerState; curr: PlatformerState; accumulator: number; lastInput: PlatformerInput }>({
    prev: createInitialPlatformerState(spawn, config, dynamicEntities),
    curr: createInitialPlatformerState(spawn, config, dynamicEntities),
    accumulator: 0,
    lastInput: NEUTRAL_INPUT,
  });
  stateRef.current = physicsRef.current.curr;

  const meshRef = useRef<THREE.Mesh>(null);
  const ghostMeshRef = useRef<THREE.Mesh>(null);
  const afterImageRef1 = useRef<THREE.Mesh>(null);
  const afterImageRef2 = useRef<THREE.Mesh>(null);
  const shakeRef = useRef(0);
  const hitStopRef = useRef(0);
  const lookaheadRef = useRef(0);
  const squashRef = useRef({ sx: 1, sy: 1 });
  const animStateRef = useRef<string>('idle');
  const animElapsedRef = useRef<number>(0);
  const isDeadRef = useRef<boolean>(false);
  const eventSeedRef = useRef(0);
  const prevGamepadButtons = useRef<boolean[]>([]);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onStateRef = useRef(onPlayerStateChange);
  onStateRef.current = onPlayerStateChange;

  useEffect(() => {
    acquireKeyboardInput();
    return () => releaseKeyboardInput();
  }, []);
  const { camera, size } = useThree();

  const sprite = map.playerSprite;
  const flipbook = useMemo(() => {
    if (!sprite || !sprite.cells.some((cell) => cell !== null)) return null;
    const canvas = drawSprite(sprite);
    return { sprite, canvas, texture: canvasTexture(canvas) };
  }, [sprite]);
  const texture = flipbook?.texture ?? null;
  useEffect(() => () => texture?.dispose(), [texture]);

  const handleEvent = (ev: PlatformerEvent, state: PlatformerState) => {
    if (ev.type === 'death') {
      shakeRef.current = 0.38;
      hitStopRef.current = 0.06;
      isDeadRef.current = true;
      squashRef.current = { sx: 1.6, sy: 0.4 };
      triggerGamepadHaptics({ duration: 250, strongMagnitude: 0.85, weakMagnitude: 0.65 });
    } else if (ev.type === 'jump') {
      squashRef.current = { sx: 0.8, sy: 1.3 };
      triggerGamepadHaptics({ duration: 80, strongMagnitude: 0.1, weakMagnitude: 0.35 });
    } else if (ev.type === 'double-jump') {
      squashRef.current = { sx: 0.75, sy: 1.35 };
      triggerGamepadHaptics({ duration: 100, strongMagnitude: 0.2, weakMagnitude: 0.45 });
    } else if (ev.type === 'wall-jump') {
      squashRef.current = { sx: 0.85, sy: 1.25 };
      triggerGamepadHaptics({ duration: 90, strongMagnitude: 0.25, weakMagnitude: 0.3 });
    } else if (ev.type === 'dash') {
      shakeRef.current = Math.max(shakeRef.current, 0.14);
      hitStopRef.current = 0.03;
      squashRef.current = { sx: 1.45, sy: 0.7 };
      triggerGamepadHaptics({ duration: 140, strongMagnitude: 0.65, weakMagnitude: 0.45 });
    } else if (ev.type === 'teleport') {
      shakeRef.current = Math.max(shakeRef.current, 0.15);
      triggerGamepadHaptics({ duration: 120, strongMagnitude: 0.25, weakMagnitude: 0.55 });
    } else if (ev.type === 'land') {
      triggerGamepadHaptics({ duration: 90, strongMagnitude: 0.35, weakMagnitude: 0.1 });
    } else if (ev.type === 'hit') {
      shakeRef.current = Math.max(shakeRef.current, 0.1);
      hitStopRef.current = Math.max(hitStopRef.current, 0.03);
      triggerGamepadHaptics({ duration: 60, strongMagnitude: 0.3, weakMagnitude: 0.2 });
    } else if (ev.type === 'hurt') {
      shakeRef.current = Math.max(shakeRef.current, 0.25);
      hitStopRef.current = Math.max(hitStopRef.current, 0.05);
      squashRef.current = { sx: 1.25, sy: 0.8 };
      triggerGamepadHaptics({ duration: 180, strongMagnitude: 0.7, weakMagnitude: 0.4 });
    } else if (ev.type === 'enemy-death') {
      shakeRef.current = Math.max(shakeRef.current, 0.12);
    } else if (ev.type === 'victory') {
      shakeRef.current = Math.max(shakeRef.current, 0.2);
      triggerGamepadHaptics({ duration: 300, strongMagnitude: 0.5, weakMagnitude: 0.8 });
    } else if (ev.type === 'respawn') {
      isDeadRef.current = false;
      squashRef.current = { sx: 1, sy: 1 };
      shakeRef.current = 0;
      hitStopRef.current = 0;
    }
    if (particlesEnabled) {
      eventSeedRef.current += 1;
      const burst = particlesForEvent(ev, eventSeedRef.current, {
        hitColour,
        playerX: state.x,
        playerY: state.y,
        playerFacing: state.facing,
      });
      if (burst) particlesRef.current = appendParticles(particlesRef.current, burst);
    }
    onEventRef.current?.(ev);
  };

  useFrame((_, frameDt) => {
    if (paused) {
      // Keep edge-triggered keys from piling up while frozen.
      readInput(bindings, prevGamepadButtons);
      return;
    }
    const dt = Math.min(frameDt, 0.1) * Math.max(0.1, Math.min(2, timeScale));
    if (hitStopRef.current > 0) {
      hitStopRef.current -= dt;
      if (shakeRef.current > 0) {
        camera.position.x += (Math.random() - 0.5) * shakeRef.current * 1.6;
        camera.position.y += (Math.random() - 0.5) * shakeRef.current * 1.6;
        shakeRef.current = Math.max(0, shakeRef.current - dt * 2.5);
      }
      return;
    }

    const live = readInput(bindings, prevGamepadButtons);
    const input: PlatformerInput = movementLocked ? NEUTRAL_INPUT : live;
    const phys = physicsRef.current;
    // Edge-triggered flags must reach exactly one physics step: latch them
    // until a step consumes them, then clear.
    const latched: PlatformerInput = {
      ...input,
      jumpPressed: input.jumpPressed || phys.lastInput.jumpPressed,
      dashPressed: input.dashPressed || phys.lastInput.dashPressed,
      teleportPressed: input.teleportPressed || phys.lastInput.teleportPressed,
      shadowFormPressed: input.shadowFormPressed || phys.lastInput.shadowFormPressed,
      ghostDoublePressed: input.ghostDoublePressed || phys.lastInput.ghostDoublePressed,
      attackPressed: input.attackPressed || phys.lastInput.attackPressed,
    };
    phys.accumulator = Math.min(phys.accumulator + dt, FIXED_DT * MAX_STEPS_PER_FRAME);
    let steps = 0;
    let stepInput = latched;
    while (phys.accumulator >= FIXED_DT && steps < MAX_STEPS_PER_FRAME) {
      phys.prev = phys.curr;
      const result = stepPlatformer(phys.curr, stepInput, grid, FIXED_DT, abilities, config, dynamicEntities, {
        weapon,
        triggers: map.triggers,
      });
      phys.curr = result.state;
      for (const ev of result.events) handleEvent(ev, result.state);
      phys.accumulator -= FIXED_DT;
      steps += 1;
      // Edges consumed by the first step of this frame.
      stepInput = { ...stepInput, jumpPressed: false, dashPressed: false, teleportPressed: false, shadowFormPressed: false, ghostDoublePressed: false, attackPressed: false };
    }
    phys.lastInput = steps > 0 ? NEUTRAL_INPUT : latched;
    stateRef.current = phys.curr;
    const state = phys.curr;
    const alpha = phys.accumulator / FIXED_DT;
    // Interpolate render position between the last two physics states,
    // except across a teleport, respawn or rewind (a jump in space).
    const jumped = Math.hypot(state.x - phys.prev.x, state.y - phys.prev.y) > 2;
    const rx = jumped ? state.x : phys.prev.x + (state.x - phys.prev.x) * alpha;
    const ry = jumped ? state.y : phys.prev.y + (state.y - phys.prev.y) * alpha;

    if (particlesEnabled && particlesRef.current.length > 0) {
      particlesRef.current = stepParticles(particlesRef.current, dt);
    }

    // Landing squash
    if (phys.prev.verticalState === 'falling' && state.verticalState === 'grounded' && steps > 0) {
      squashRef.current = { sx: 1.35, sy: 0.7 };
    }
    const springSpeed = Math.min(1, 16.0 * dt);
    squashRef.current.sx += (1.0 - squashRef.current.sx) * springSpeed;
    squashRef.current.sy += (1.0 - squashRef.current.sy) * springSpeed;

    onStateRef.current?.(state);

    const targetAnim = selectPose(state, input, isDeadRef.current, sprite);
    if (animStateRef.current !== targetAnim) {
      animStateRef.current = targetAnim;
      animElapsedRef.current = 0;
    } else {
      animElapsedRef.current += dt;
    }
    if (flipbook && sprite) {
      drawSpriteCells(flipbook.canvas, sprite.size, spriteAnimationCells(sprite, animStateRef.current, animElapsedRef.current));
      flipbook.texture.needsUpdate = true;
    }

    if (meshRef.current) {
      const facingSign = state.facing === 'left' ? -1 : 1;
      const spriteScale = (sprite ? sprite.size / 16 : 1) * TILE_SIZE * 0.95;
      meshRef.current.position.set(rx, -ry + spriteAnchorLift(sprite, spriteScale * squashRef.current.sy, PLAYER_HALF_HEIGHT), 0.1);
      meshRef.current.scale.set(facingSign * squashRef.current.sx * spriteScale, squashRef.current.sy * spriteScale, 1);
      const mat = meshRef.current.material as THREE.MeshBasicMaterial;
      if (state.shadowFormTimer > 0) {
        mat.opacity = 0.55;
      } else if (state.invulnTimer > 0 && !state.isDead) {
        mat.opacity = Math.floor(state.invulnTimer * 12) % 2 === 0 ? 1.0 : 0.35;
      } else {
        mat.opacity = 1.0;
      }
    }

    const isSpecialForm = state.dashTimer > 0 || state.shadowFormTimer > 0 || state.verticalState === 'rewinding';
    if (afterImageRef1.current && afterImageRef2.current) {
      if (isSpecialForm && state.history.length >= 4) {
        const hist1 = state.history[state.history.length - 2];
        const hist2 = state.history[state.history.length - 4];
        const spriteScale = (sprite ? sprite.size / 16 : 1) * TILE_SIZE * 0.95;
        const lift = spriteAnchorLift(sprite, spriteScale, PLAYER_HALF_HEIGHT);
        if (hist1) {
          afterImageRef1.current.visible = true;
          afterImageRef1.current.position.set(hist1.x, -hist1.y + lift, 0.08);
          afterImageRef1.current.scale.set((hist1.facing === 'left' ? -1 : 1) * spriteScale, spriteScale, 1);
        }
        if (hist2) {
          afterImageRef2.current.visible = true;
          afterImageRef2.current.position.set(hist2.x, -hist2.y + lift, 0.07);
          afterImageRef2.current.scale.set((hist2.facing === 'left' ? -1 : 1) * spriteScale, spriteScale, 1);
        }
      } else {
        afterImageRef1.current.visible = false;
        afterImageRef2.current.visible = false;
      }
    }

    if (ghostMeshRef.current) {
      if (state.ghostDouble?.active) {
        const spriteScale = (sprite ? sprite.size / 16 : 1) * TILE_SIZE * 0.95;
        ghostMeshRef.current.visible = true;
        ghostMeshRef.current.position.set(state.ghostDouble.x, -state.ghostDouble.y + spriteAnchorLift(sprite, spriteScale, PLAYER_HALF_HEIGHT), 0.09);
        ghostMeshRef.current.scale.set((state.ghostDouble.facing === 'left' ? -1 : 1) * spriteScale, spriteScale, 1);
      } else {
        ghostMeshRef.current.visible = false;
      }
    }

    if (follow) {
      const targetLookahead = state.facing === 'right' ? 2.2 : -2.2;
      const lookaheadLerp = 1 - Math.exp(-dt * 6.0);
      lookaheadRef.current += (targetLookahead - lookaheadRef.current) * lookaheadLerp;

      const orthoCam = camera as THREE.OrthographicCamera;
      const zoom = 'zoom' in orthoCam && typeof orthoCam.zoom === 'number' ? orthoCam.zoom : PIXELS_PER_TILE;
      const halfW = size.width / (2 * zoom);
      const halfH = size.height / (2 * zoom);
      const clampX = (v: number) => (map.width <= halfW * 2 ? map.width / 2 : Math.max(halfW, Math.min(map.width - halfW, v)));
      const clampY = (v: number) => (map.height <= halfH * 2 ? map.height / 2 : Math.max(halfH, Math.min(map.height - halfH, v)));
      const targetX = clampX(rx + lookaheadRef.current);
      const targetY = -clampY(ry);
      const followLerp = 1 - Math.exp(-dt * 7.67);
      camera.position.x += (targetX - camera.position.x) * followLerp;
      camera.position.y += (targetY - camera.position.y) * followLerp;

      // 52/42/48 originally; kept as ratios of the resting zoom so a game
      // that frames tighter keeps the same pull-back when it runs and the
      // same push-in on a wall.
      const restZoom = cameraZoom;
      const targetZoom =
        state.verticalState === 'wall-sliding'
          ? restZoom * (52 / 48)
          : Math.abs(state.vx) > 3.5 || state.verticalState === 'gliding'
            ? restZoom * (42 / 48)
            : restZoom;
      if ('zoom' in orthoCam && typeof orthoCam.zoom === 'number') {
        const zoomLerp = 1 - Math.exp(-dt * 3.71);
        orthoCam.zoom += (targetZoom - orthoCam.zoom) * zoomLerp;
        orthoCam.updateProjectionMatrix();
      }

      if (shakeRef.current > 0) {
        camera.position.x += (Math.random() - 0.5) * shakeRef.current * 1.6;
        camera.position.y += (Math.random() - 0.5) * shakeRef.current * 1.6;
        shakeRef.current = Math.max(0, shakeRef.current - dt * 2.2);
      }
    }
  });

  const spriteScale = (sprite ? sprite.size / 16 : 1) * TILE_SIZE * 0.95;
  return (
    <>
      <mesh ref={meshRef} position={[spawn.x, -spawn.y, 0.1]} scale={[spriteScale, spriteScale, 1]}>
        <planeGeometry args={[1, 1]} />
        {texture ? <meshBasicMaterial map={texture} transparent toneMapped={false} /> : <meshBasicMaterial color="#18181b" toneMapped={false} />}
      </mesh>
      <mesh ref={afterImageRef1} position={[spawn.x, -spawn.y, 0.08]} visible={false}>
        <planeGeometry args={[1, 1]} />
        {texture ? (
          <meshBasicMaterial map={texture} transparent opacity={0.35} color="#38bdf8" toneMapped={false} />
        ) : (
          <meshBasicMaterial color="#38bdf8" transparent opacity={0.35} toneMapped={false} />
        )}
      </mesh>
      <mesh ref={afterImageRef2} position={[spawn.x, -spawn.y, 0.07]} visible={false}>
        <planeGeometry args={[1, 1]} />
        {texture ? (
          <meshBasicMaterial map={texture} transparent opacity={0.2} color="#c084fc" toneMapped={false} />
        ) : (
          <meshBasicMaterial color="#c084fc" transparent opacity={0.2} toneMapped={false} />
        )}
      </mesh>
      <mesh ref={ghostMeshRef} position={[spawn.x, -spawn.y, 0.09]} visible={false}>
        <planeGeometry args={[1, 1]} />
        {texture ? (
          <meshBasicMaterial map={texture} transparent opacity={0.45} color="#38bdf8" toneMapped={false} />
        ) : (
          <meshBasicMaterial color="#38bdf8" transparent opacity={0.45} toneMapped={false} />
        )}
      </mesh>
    </>
  );
}

export interface Platformer2DSceneProps {
  map: Tilemap2D;
  mode?: 'play' | 'edit';
  abilities?: PlatformerAbilities;
  config?: PlatformerConfig;
  dynamicEntities?: DynamicEntity[];
  /** Optional baked replacement for this map's procedural parallax skybox.
   * See {@link BackdropArt}. */
  backdropArt?: BackdropArt;
  /** See `PlatformerPlayer`'s own prop of the same name. Ignored outside
   * `mode: 'play'`. */
  movementLocked?: boolean;
  /** The equipped weapon; null or undefined disables attacking. */
  weapon?: WeaponSpec | null;
  /** Keyboard and gamepad layout overrides on top of
   * `DEFAULT_PLATFORMER_BINDINGS`. */
  bindings?: PlatformerBindingsOverrides;
  /** Particle bursts on hits, landings and pickups. Default on. */
  particles?: { enabled?: boolean };
  /**
   * How many screen pixels one tile occupies at rest, which is what decides
   * how much of the level is in shot. Defaults to 48; a 1920-wide viewport
   * then shows 40 tiles, so a one-tile character is about 2.5% of the
   * frame's width - wide enough to read the level, and wide enough that the
   * player themselves is hard to pick out.
   *
   * Exposed as a prop rather than retuned in place because this scene is
   * `@sstk/build-kit`'s shared platformer and every consumer's framing would
   * move with it. The speed and wall-slide zooms scale with this, keeping
   * their existing relationship to it.
   */
  cameraZoom?: number;
  /** Baked art for projectiles, keyed by `ProjectileSpec.kind`. */
  projectileArt?: Record<string, Sprite2D>;
  /** Colour of hit sparks (the weapon's accent). */
  hitColour?: string;
  /** Freeze the simulation (a pause menu) without unmounting the player. */
  paused?: boolean;
  /** Simulation speed multiplier, clamped to 0.1..2. Default 1. */
  timeScale?: number;
  /** Called once per rendered frame with the latest physics state. */
  onPlayerStateChange?: (state: PlatformerState) => void;
  onEvent?: (event: PlatformerEvent) => void;
}

/** Mount inside a Canvas: 2D side-scrolling platformer scene. */
export function Platformer2DScene({
  map,
  mode = 'play',
  abilities,
  config,
  dynamicEntities,
  backdropArt,
  movementLocked,
  weapon,
  bindings,
  particles,
  projectileArt,
  hitColour,
  paused,
  timeScale,
  cameraZoom = PIXELS_PER_TILE,
  onPlayerStateChange,
  onEvent,
}: Platformer2DSceneProps) {
  const centre: [number, number, number] = [(map.width * TILE_SIZE) / 2, -(map.height * TILE_SIZE) / 2, 10];
  const stateRef = useRef<PlatformerState | null>(null);
  const particlesRef = useRef<Particle2D[]>([]);
  const mergedBindings = useMemo(() => (bindings ? mergePlatformerBindings(bindings) : DEFAULT_PLATFORMER_BINDINGS), [bindings]);
  const particlesEnabled = particles?.enabled !== false;

  return (
    <>
      <OrthographicCamera makeDefault position={centre} zoom={cameraZoom} near={0.1} far={100} />
      <ambientLight intensity={1} />
      <ParallaxBackground
        mapWidth={map.width}
        mapHeight={map.height}
        theme={map.environmentTheme || 'rooftops'}
        backdropArt={backdropArt}
      />
      <ChunkedTilemapLayer map={map} layer="main" z={0} />
      <PointLightRadianceMesh map={map} />
      <WorldEntities map={map} />
      <PatrollingEntities map={map} />
      <DynamicEntitiesMesh dynamicEntities={dynamicEntities} stateRef={stateRef} />
      <PickupsLayer dynamicEntities={dynamicEntities} stateRef={stateRef} />
      <EnemiesLayer dynamicEntities={dynamicEntities} stateRef={stateRef} />
      <ProjectilesLayer stateRef={stateRef} projectileArt={projectileArt} />
      {mode === 'play' && (
        <PlatformerPlayer
          map={map}
          abilities={abilities}
          config={config}
          dynamicEntities={dynamicEntities}
          follow
          movementLocked={movementLocked}
          weapon={weapon}
          bindings={mergedBindings}
          stateRef={stateRef}
          particlesRef={particlesRef}
          particlesEnabled={particlesEnabled}
          hitColour={hitColour}
          cameraZoom={cameraZoom}
          paused={paused}
          timeScale={timeScale}
          onPlayerStateChange={onPlayerStateChange}
          onEvent={onEvent}
        />
      )}
      {particlesEnabled && <ParticlesLayer particlesRef={particlesRef} />}
      <ChunkedTilemapLayer map={map} layer="fg" z={0.15} />
    </>
  );
}
